import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

// Ambient Indian classical music toggle for the navbar.
// - Starts MUTED/paused on every load — never autoplays with sound.
// - Click to play (volume 0.3, looped); click again to pause.
// - Fallback chaining: if a source URL fails, the next is tried automatically.
// - State persisted in sessionStorage for the current session.
const SOURCES = [
  'https://cdn.pixabay.com/audio/2024/07/22/audio_6374c52c1d.mp3',
  'https://cdn.pixabay.com/audio/2023/01/16/audio_7aa1dd56d0.mp3',
  'https://cdn.pixabay.com/audio/2022/11/22/audio_0e7cdd3019.mp3',
  'https://cdn.pixabay.com/audio/2022/10/30/audio_946e99370e.mp3',
];

const MusicToggle = () => {
  const audioRef = useRef(null);
  const srcIdxRef = useRef(0);
  const [playing, setPlaying] = useState(false);

  const ensureAudio = useCallback(() => {
    if (audioRef.current) return audioRef.current;
    const audio = new Audio();
    audio.loop = true;
    audio.volume = 0.3;
    audio.preload = 'auto';
    audio.onerror = () => {
      // Advance to the next fallback source and retry.
      if (srcIdxRef.current < SOURCES.length - 1) {
        srcIdxRef.current += 1;
        audio.src = SOURCES[srcIdxRef.current];
        audio.load();
        if (playing) audio.play().catch(() => {});
      }
    };
    audio.src = SOURCES[0];
    audioRef.current = audio;
    return audio;
  }, [playing]);

  const toggle = useCallback(() => {
    const audio = ensureAudio();
    if (playing) {
      audio.pause();
      setPlaying(false);
      try { sessionStorage.setItem('shaktiverse_music_on', '0'); } catch (_) {}
    } else {
      audio.volume = 0.3;
      audio.play().then(() => {
        setPlaying(true);
        try { sessionStorage.setItem('shaktiverse_music_on', '1'); } catch (_) {}
      }).catch(() => {
        // Try next source once if the first cannot play.
        if (srcIdxRef.current < SOURCES.length - 1) {
          srcIdxRef.current += 1;
          audio.src = SOURCES[srcIdxRef.current];
          audio.load();
          audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
        }
      });
    }
  }, [playing, ensureAudio]);

  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={playing ? 'Pause ambient music' : 'Play ambient music'}
      title={playing ? 'Pause ambient music' : 'Play ambient music'}
      className="text-primary/70 hover:text-primary transition-colors p-1.5 rounded-full hover:bg-primary/10"
      data-testid="music-toggle-button"
    >
      {playing ? <Volume2 size={18} /> : <VolumeX size={18} />}
    </button>
  );
};

export default MusicToggle;
